import { type ChangeEvent, createContext, type FC, type MouseEvent, type ReactElement, useState } from 'react';
import { usePagination } from '../content/hooks';
import type {
  ClientModel,
  Order,
  RoleModel,
  UserFiltersProps,
  UserListModel,
  UserSortingFields,
  VenueModel
} from '../models';

interface IUsersContext {
  usersUpdate: boolean;
  toggleUsersUpdate: () => void;
  users: UserListModel[];
  setUsers: (val: UserListModel[]) => void;
  clients: ClientModel[];
  setClients: (val: ClientModel[]) => void;
  page: number;
  setPage: (val: number) => void;
  limit: number;
  setLimit: (val: number) => void;
  count: number;
  setCount: (val: number) => void;
  order: Order;
  setOrder: (val: Order) => void;
  orderBy: UserSortingFields;
  setOrderBy: (val: UserSortingFields) => void;
  handlePageChange: (_event: any, newPage: number) => void;
  handleLimitChange: (event: ChangeEvent<HTMLInputElement>) => void;
  handleRequestSort: (_event: MouseEvent, property: UserSortingFields) => void;
  loading: boolean;
  setLoading: (val: boolean) => void;
  setRoles: (val: RoleModel[]) => void;
  roles: RoleModel[];
  venues: VenueModel[];
  setVenues: (val: VenueModel[]) => void;
  setFilters: (val: UserFiltersProps) => void;
  filters: UserFiltersProps;
  resetFilters: () => void;
}

export const UsersContext = createContext<IUsersContext>({} as IUsersContext);

export const UsersProvider: FC<{ children: ReactElement }> = ({ children }) => {
  const [usersUpdate, setUsersUpdate] = useState(false);
  const toggleUpdate = (): void => setUsersUpdate(!usersUpdate);

  const {
    page,
    setPage,
    limit,
    setLimit,
    count,
    setCount,
    order,
    setOrder,
    orderBy,
    setOrderBy,
    handlePageChange,
    handleLimitChange,
    handleRequestSort
  } = usePagination<UserSortingFields>(toggleUpdate, 'fullName', 'asc');

  const [users, setUsers] = useState<UserListModel[]>([]);
  const [clients, setClients] = useState<ClientModel[]>([]);
  const [loading, setLoading] = useState(false);
  const [roles, setRoles] = useState<RoleModel[]>([]);
  const [venues, setVenues] = useState<VenueModel[]>([]);
  const emptyFilters: UserFiltersProps = {
    firstName: undefined,
    lastName: undefined,
    email: undefined,
    mobilePhone: undefined,
    jobRole: undefined,
    roles: undefined,
    venues: undefined,
    clients: undefined,
    status: undefined
  };
  const [filters, setFilters] = useState<UserFiltersProps>(emptyFilters);
  const resetFilters = (): void => setFilters(emptyFilters);

  return (
    <UsersContext.Provider
      value={{
        usersUpdate,
        resetFilters,
        toggleUsersUpdate: toggleUpdate,
        users,
        setUsers,
        clients,
        setClients,
        page,
        setPage,
        limit,
        setLimit,
        count,
        setCount,
        order,
        setOrder,
        orderBy,
        setOrderBy,
        handlePageChange,
        handleLimitChange,
        handleRequestSort,
        loading,
        setLoading,
        roles,
        setRoles,
        filters,
        setFilters,
        venues,
        setVenues
      }}
    >
      {children}
    </UsersContext.Provider>
  );
};
